import React from "react";
import { type StaticImageData } from "next/image";
import { Box, type SpaceProps } from "@chakra-ui/react";
import { NavatticDemoModal } from "components/demo-embed";
import { trackDemoButtonClicked } from "components/event-tracking";
import { type Theme } from "contexts/color-theme";
import { RuleAndTextCta } from "ui/text";
import { useDemoLinkWithParameters } from "hooks/use-demo-link";
import { DirectionalButton } from "ui/button";
import { ProductShowcase, ProductShowcaseImage, type ProductShowcaseProps } from "ui/product-showcase";
import defaultProductImage from "../../../public/assets/demo-embed/audiences.png";
import defaultMobileProductImage from "../../../public/assets/demo-embed/audiences-mobile.png";

type ProductDemoProps = {
  /**
   * Determines the text color.
   * @default "light"
   */
  theme?: Theme;

  /**
   * If true, only the demo link variation will be displayed, which typically only shows up on mobile screens.
   * @default false
   */
  hasDemoLinkOnly?: boolean;

  /**
   * Variant of the product showcase image.
   * @default "sunset"
   */
  showcaseImageVariant?: ProductShowcaseProps["variant"];

  /**
   * Text to display on desktop screens when the navattic demo modal is used.
   * @default "Explore our virtual tour to learn how to quickly create and activate identity-enhanced audiences directly from your data warehouse."
   */
  demoEmbedText?: string;

  /**
   * Text to display on the CTA button for the demo embed.
   * @default "Try it out"
   */
  demoEmbedCtaText?: string;

  /**
   * Text to display on mobile screens when the demo link is used.
   * @default "Talk to a product expert to learn how to quickly create and activate identity-enhanced audiences today."
   */
  demoLinkText?: string;

  /**
   * Override the product image to display on desktop screens.
   */
  productImage?: StaticImageData;

  /**
   * Override the product image alt text on desktop screens.
   */
  productImageAlt?: string;

  /**
   * Override the product image to display on mobile screens.
   */
  mobileProductImage?: StaticImageData;

  /**
   * Override the product image alt text on mobile screens.
   */
  mobileProductImageAlt?: string;
} & SpaceProps;

export function ProductDemo({
  hasDemoLinkOnly,
  theme = "light",
  showcaseImageVariant = "sunset",
  demoEmbedText = "Explore our virtual tour to learn how to quickly create and activate identity-enhanced audiences directly from your data warehouse.",
  demoEmbedCtaText = "Try it out",
  demoLinkText = "Talk to a product expert to learn how to quickly create and activate identity-enhanced audiences today.",
  productImage = defaultProductImage,
  mobileProductImage = defaultMobileProductImage,
  productImageAlt = "Hightouch Audiences user interface.",
  mobileProductImageAlt = "Hightouch Audiences user interface.",
  ...spaceProps
}: ProductDemoProps) {
  const demoLink = useDemoLinkWithParameters();

  return (
    <Box {...spaceProps}>
      <ProductShowcase
        variant={showcaseImageVariant}
        productScreen={<ProductShowcaseImage src={productImage} alt={productImageAlt} />}
        mobileProductScreen={<ProductShowcaseImage isMobile src={mobileProductImage} alt={mobileProductImageAlt} />}
      />

      {!hasDemoLinkOnly && (
        <RuleAndTextCta
          hasRuleHidden
          theme={theme}
          mt="big"
          display={{ base: "none", md: "flex" }}
          action={<NavatticDemoModal buttonType="directional">{demoEmbedCtaText}</NavatticDemoModal>}
        >
          {demoEmbedText}
        </RuleAndTextCta>
      )}

      <RuleAndTextCta
        hasRuleHidden
        display={{ base: "flex", md: hasDemoLinkOnly ? "flex" : "none" }}
        theme={theme}
        mt="small"
        action={
          <DirectionalButton href={demoLink} onClick={trackDemoButtonClicked}>
            Get a demo
          </DirectionalButton>
        }
      >
        {demoLinkText}
      </RuleAndTextCta>
    </Box>
  );
}
